import { useCallback, useEffect, useState } from 'react';
import { ImageFormat } from '../../types/media';
import {
  FORMAT_LABELS,
  getConversionDescription,
  getConversionTitle,
} from '../../config/conversions';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { clearError, runConversion, setFileMeta, setFormats } from '../../store/conversionSlice';
import FileUpload from '../FileUpload/FileUpload';
import FormatBadge from '../FormatBadge/FormatBadge';
import AdPlaceholder from '../AdPlaceholder/AdPlaceholder';
import styles from './ConversionTool.module.css';

interface ConversionToolProps {
  from: ImageFormat;
  to: ImageFormat;
}

export default function ConversionTool({ from, to }: ConversionToolProps) {
  const dispatch = useAppDispatch();
  const { fileMeta, loading, progress, error, resultUrl, resultFileName } = useAppSelector(
    (state) => state.conversion,
  );

  const [file, setFile] = useState<File | null>(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    dispatch(setFormats({ from, to }));
  }, [dispatch, from, to]);

  useEffect(() => {
    return () => {
      if (filePreviewUrl) {
        URL.revokeObjectURL(filePreviewUrl);
      }
    };
  }, [filePreviewUrl]);

  const handleFileSelect = useCallback(
    (selected: File | null) => {
      if (filePreviewUrl) {
        URL.revokeObjectURL(filePreviewUrl);
      }
      setFile(selected);
      setFilePreviewUrl(selected ? URL.createObjectURL(selected) : null);
      dispatch(
        setFileMeta(
          selected
            ? {
                fileName: selected.name,
                fileSize: selected.size,
                fileType: selected.type,
                lastModified: selected.lastModified,
              }
            : null,
        ),
      );
    },
    [dispatch, filePreviewUrl],
  );

  const handleConvert = () => {
    if (!file) return;
    dispatch(clearError());
    dispatch(runConversion({ file, fromFormat: from, toFormat: to }));
  };

  const handleDownload = () => {
    if (!resultUrl) return;
    const link = document.createElement('a');
    link.href = resultUrl;
    link.download = resultFileName || `converted.${to === 'jpeg' ? 'jpg' : to}`;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const hasFile = Boolean(file && fileMeta);

  return (
    <article className={styles.tool}>
      <header className={styles.hero}>
        <h1>{getConversionTitle(from, to)}</h1>
        <p className={styles.subtitle}>{getConversionDescription(from, to)}</p>
        <div className={styles.formatFlow} aria-label="Conversion direction">
          <FormatBadge format={from} variant="from" />
          <span className={styles.arrow} aria-hidden>
            →
          </span>
          <FormatBadge format={to} variant="to" />
        </div>
      </header>

      {/*<AdPlaceholder slot="inline" />*/}

      <section className={styles.panel} aria-labelledby="upload-heading">
        <h2 id="upload-heading" className={styles.sectionTitle}>
          1. Upload your {FORMAT_LABELS[from]} file
        </h2>
        <FileUpload
          sourceFormat={from}
          file={file}
          previewUrl={filePreviewUrl}
          disabled={loading}
          onFileSelect={handleFileSelect}
        />
      </section>

      <section className={styles.panel} aria-labelledby="convert-heading">
        <h2 id="convert-heading" className={styles.sectionTitle}>
          2. Convert to {FORMAT_LABELS[to]}
        </h2>
        <button
          type="button"
          className={styles.convertBtn}
          disabled={!hasFile || loading}
          onClick={handleConvert}
        >
          {loading ? `Converting… ${progress}%` : `Convert to ${FORMAT_LABELS[to]}`}
        </button>

        {loading && (
          <div className={styles.progressWrap} role="progressbar" aria-valuenow={progress}>
            <div className={styles.progressBar} style={{ width: `${progress}%` }} />
          </div>
        )}

        {error && (
          <div className={styles.error} role="alert">
            <p>{error}</p>
            <button type="button" onClick={() => dispatch(clearError())}>
              Dismiss
            </button>
          </div>
        )}
      </section>

      {resultUrl && (
        <section className={styles.result} aria-labelledby="result-heading">
          <h2 id="result-heading" className={styles.sectionTitle}>
            3. Download your {FORMAT_LABELS[to]}
          </h2>
          <p className={styles.success}>Conversion complete!</p>
          <button type="button" className={styles.downloadBtn} onClick={handleDownload}>
            Download {resultFileName || `${FORMAT_LABELS[to]} file`}
          </button>
        </section>
      )}
    </article>
  );
}
